import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  MdDelete,
  MdEdit,
  MdSearch,
  MdClose,
  MdVisibility,
  MdArticle,
} from 'react-icons/md';
import { jsPDF } from 'jspdf';
import html2canvas from 'html2canvas';
import AudioMixer from 'components/audio/AudioMixer';

const sanitizeText = (text) => {
  return text
    .replace(/[\u2018\u2019]/g, "'")
    .replace(/[\u201C\u201D]/g, '"')
    .replace(/[\u2013\u2014]/g, '-')
    .replace(/[\u2022]/g, '*')
    .replace(/\r\n/g, '\n');
};

const convertTextToPDF = async (title, content) => {
  const container = document.createElement('div');
  container.style.cssText = `
    position: fixed;
    top: -9999px;
    left: -9999px;
    width: 794px;
    padding: 60px;
    background: white;
    font-family: 'Noto Sans', 'Noto Sans Devanagari', Arial, sans-serif;
    font-size: 14px;
    line-height: 1.8;
    color: #282828;
    box-sizing: border-box;
  `;

  const escapedTitle = title.replace(/</g, '&lt;').replace(/>/g, '&gt;');
  const escapedContent = content
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/\n/g, '<br/>');

  container.innerHTML = `
    <h1 style="font-size:22px; font-weight:bold; margin:0 0 10px 0;
               font-family:'Noto Sans','Noto Sans Devanagari',Arial,sans-serif;">
      ${escapedTitle}
    </h1>
    <div style="border-top: 2px solid #4318FF; margin-bottom: 20px;"></div>
    <div style="white-space: pre-wrap;
                font-family:'Noto Sans','Noto Sans Devanagari',Arial,sans-serif;
                font-size:13px; line-height:1.8;">
      ${escapedContent}
    </div>
  `;

  document.body.appendChild(container);

  try {
    const canvas = await html2canvas(container, {
      scale: 2,
      useCORS: true,
      backgroundColor: '#ffffff',
      windowWidth: 794,
    });

    const imgData = canvas.toDataURL('image/png');
    const pdf = new jsPDF({ unit: 'pt', format: 'a4' });

    const pageWidth = pdf.internal.pageSize.getWidth();
    const pageHeight = pdf.internal.pageSize.getHeight();
    const imgHeight = (canvas.height * pageWidth) / canvas.width;

    let heightLeft = imgHeight;
    let position = 0;

    pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
    heightLeft -= pageHeight;

    while (heightLeft > 0) {
      position -= pageHeight;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, pageWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    const blob = pdf.output('blob');
    const cleanTitle =
      title.replace(/\s+/g, '_').replace(/[^\w_]/g, '') || 'chapter';
    return new File([blob], `${cleanTitle}.pdf`, { type: 'application/pdf' });
  } finally {
    document.body.removeChild(container);
  }
};

const BASE_URL =
  process.env.REACT_APP_BASE_URL?.replace(/\/$/, '') || 'http://localhost:3030';
const API_BASE = `${BASE_URL}/api/admin`;
const USER_BASE = `${BASE_URL}/api/users`;

const getAssetUrl = (filePath) => {
  if (!filePath) return '';
  if (/^https?:\/\//i.test(filePath)) return filePath;
  const normalizedPath = String(filePath).startsWith('/')
    ? String(filePath)
    : `/${filePath}`;
  return `${BASE_URL}${normalizedPath}`;
};

const EMPTY_CHAPTER = {
  title: '',
  description: '',
  image: null,
  audio: null,
  audioFile: null,
  textFile: null,
  textContent: '',
  pdfFile: null,
  pdfReady: false,
  pdfDirty: false,
  isPaid: false,
  diamondPrice: 0,
};

const isUploadFile = (value) =>
  typeof File !== 'undefined' && value instanceof File;

const normalizePrice = (value) => {
  const price = Number(value);
  return Number.isFinite(price) && price > 0 ? price : 0;
};

const S = {
  page: {
    padding: '100px 36px 36px',
    background: 'linear-gradient(180deg, #F7F9FF 0%, #F3F6FF 100%)',
    minHeight: '100vh',
    fontFamily:
      "Inter, system-ui, -apple-system, 'Segoe UI', Roboto, 'Helvetica Neue', Arial",
    color: '#0F1724',
    WebkitFontSmoothing: 'antialiased',
    MozOsxFontSmoothing: 'grayscale',
  },
  container: {
    maxWidth: '1200px',
    margin: '0 auto',
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  card: {
    background: '#FFFFFF',
    borderRadius: '14px',
    padding: '22px',
    boxShadow: '0 8px 24px rgba(16,24,40,0.06)',
    border: '1px solid rgba(15,23,42,0.04)',
    transition: 'transform 180ms ease, box-shadow 180ms ease',
  },
  input: {
    width: '100%',
    padding: '12px 14px',
    marginBottom: '12px',
    borderRadius: '10px',
    border: '1px solid #E6EEF8',
    outline: 'none',
    boxSizing: 'border-box',
    background: '#FBFDFF',
    color: '#0F1724',
  },
  btnPrimary: {
    background: '#4318FF',
    color: '#fff',
    border: 'none',
    padding: '12px 22px',
    borderRadius: '12px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 20px rgba(67,24,255,0.12)',
    transition: 'transform 120ms ease, box-shadow 120ms ease',
  },
  btnGreen: {
    background: '#0FBF7C',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 6px 16px rgba(15,191,124,0.12)',
  },
  btnSecondary: {
    background: '#EDF2F7',
    color: '#1B254B',
    border: 'none',
    padding: '10px 16px',
    borderRadius: '10px',
    fontWeight: 700,
    cursor: 'pointer',
  },
  btnPDF: {
    background: 'linear-gradient(90deg,#FF6B35 0%, #FF8A4B 100%)',
    color: '#fff',
    border: 'none',
    padding: '10px 18px',
    borderRadius: '10px',
    fontWeight: 700,
    cursor: 'pointer',
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    boxShadow: '0 8px 20px rgba(255,107,53,0.12)',
  },
  modal: {
    position: 'fixed',
    inset: 0,
    background: 'rgba(10,11,13,0.55)',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    zIndex: 1000,
    backdropFilter: 'blur(6px)',
  },
  modalBox: {
    background: '#fff',
    padding: '28px',
    borderRadius: '16px',
    width: '1000px',
    maxWidth: 'calc(100% - 48px)',
    maxHeight: '92vh',
    overflowY: 'auto',
    position: 'relative',
    boxShadow: '0 24px 48px rgba(11,15,40,0.16)',
  },
  chapterRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    background: '#FFFFFF',
    padding: '12px 16px',
    borderRadius: '12px',
    marginBottom: '10px',
    border: '1px solid rgba(15,23,42,0.04)',
    boxShadow: '0 6px 18px rgba(15,23,42,0.04)',
  },
  bulkTab: {
    padding: '7px 18px',
    borderRadius: '8px',
    border: 'none',
    fontWeight: 700,
    fontSize: '13px',
    cursor: 'pointer',
    transition: 'all 160ms ease',
  },
  bulkRow: {
    background: '#fff',
    borderRadius: '14px',
    border: '1px solid #E0E5F2',
    padding: '14px 16px',
    marginBottom: '12px',
    boxShadow: '0 4px 12px rgba(15,23,42,0.04)',
  },
};

const StoryManagementAdmin = () => {
  const [loading, setLoading] = useState(false);
  const [loadingSeconds, setLoadingSeconds] = useState(0);
  const [categories, setCategories] = useState([]);
  const [allStories, setAllStories] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');

  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [viewStory, setViewStory] = useState(null);
  const [viewStoryLoading, setViewStoryLoading] = useState(false);
  const [showDeleted, setShowDeleted] = useState(false);
  const [showDeletedChapters, setShowDeletedChapters] = useState(false);

  const [storyData, setStoryData] = useState({
    title: '',
    description: '',
    category: '',
    coverImage: null,
    existingCoverImage: '',
  });

  const [chapters, setChapters] = useState([]);
  const [draft, setDraft] = useState({ ...EMPTY_CHAPTER });
  const [editingChapterIndex, setEditingChapterIndex] = useState(null);
  const [approveOnUpdate, setApproveOnUpdate] = useState(false);
  const [chapterMode, setChapterMode] = useState('single'); // 'single' | 'bulk'
  const [bulkRows, setBulkRows] = useState([]);
  const chapterDraftRef = useRef(null);
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    fetchInitialData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showDeleted]);

  // ⏱ Loader timer — story add/update ke time chalta hai
  useEffect(() => {
    if (!loading) {
      setLoadingSeconds(0);
      return;
    }
    const interval = setInterval(() => {
      setLoadingSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [loading]);

  const fetchInitialData = async () => {
    try {
      setLoading(true);
      const [catRes, storyRes] = await Promise.all([
        axios.get(`${USER_BASE}/categories`),
        axios.get(`${API_BASE}/user-stories`, {
          headers,
          params: { includeDeleted: showDeleted },
        }),
      ]);
      setCategories(catRes.data.data || []);
      setAllStories(storyRes.data.data || []);
    } catch (err) {
      console.error('Fetch failed', err);
    } finally {
      setLoading(false);
    }
  };

  const deleteStory = async (id) => {
    if (!window.confirm('❗ Trash this story? You can restore it anytime.'))
      return;
    try {
      await axios.delete(`${API_BASE}/stories/${id}`, { headers });
      fetchInitialData();
    } catch {
      alert('Delete failed');
    }
  };

  const restoreStory = async (id) => {
    try {
      await axios.patch(`${API_BASE}/stories/${id}/restore`, {}, { headers });
      fetchInitialData();
    } catch {
      alert('Restore failed');
    }
  };

  const deleteChapterFromView = async (storyId, chapterId) => {
    if (
      !window.confirm(
        '❗ Move this chapter to trash? It can be restored later.',
      )
    )
      return;
    try {
      await axios.delete(
        `${API_BASE}/stories/${storyId}/chapters/${chapterId}`,
        { headers },
      );
      const res = await axios.get(`${API_BASE}/user-stories/${storyId}`, {
        headers,
      });
      if (res.data?.data) setViewStory(res.data.data);
      fetchInitialData();
    } catch (err) {
      console.error(err);
      alert('Chapter delete failed');
    }
  };

  const restoreChapter = async (storyId, chapterId) => {
    try {
      await axios.patch(
        `${API_BASE}/stories/${storyId}/chapters/${chapterId}/restore`,
        {},
        { headers },
      );
      const res = await axios.get(`${API_BASE}/user-stories/${storyId}`, {
        headers,
      });
      if (res.data?.data) setViewStory(res.data.data);
      fetchInitialData();
    } catch (err) {
      console.error(err);
      alert('Chapter restore failed');
    }
  };

  const handleToggleFlag = async (storyId, field, currentValue) => {
    try {
      const payload =
        field === 'isPopular'
          ? { isPopular: !currentValue }
          : { isRecommended: !currentValue };

      const res = await axios.patch(
        `${API_BASE}/stories/${storyId}/toggle-flags`,
        payload,
        { headers },
      );

      setAllStories((prev) =>
        prev.map((story) =>
          story._id === storyId ? { ...story, ...res.data.data } : story,
        ),
      );
    } catch (err) {
      console.error(err);
      alert('❌ Status update failed');
    }
  };

  const toChapterId = (id) => (id ? String(id) : null);

  const normalizeStoryChapters = (story) =>
    (story.chapters || [])
      .filter((ch) => !ch.isDeleted)
      .map((ch) => ({
        ...EMPTY_CHAPTER,
        ...ch,
        _id: toChapterId(ch._id),
        audio: null,
        pdfFile: null,
        audioFile: ch.audioFile || null,
        textFile: ch.textFile || null,
        textContent: ch.textContent || ch.content || '',
        isExisting: true,
        // ✅ FIX: pdfReady = true if server already has a PDF (user-uploaded or admin-generated)
        pdfReady: !!(ch.textFile || ch.pdfFile),
        pdfDirty: false,
      }));

  const openViewStory = async (story) => {
    setViewStoryLoading(true);
    setViewStory({ ...story, chapters: story.chapters || [] });
    try {
      const res = await axios.get(`${API_BASE}/user-stories/${story._id}`, {
        headers,
      });
      if (res.data?.data) {
        setViewStory(res.data.data);
      }
    } catch (err) {
      console.error('Failed to load story chapters', err);
    } finally {
      setViewStoryLoading(false);
    }
  };

  const buildDraftFromChapter = (chapter) => {
    const isExistingChapter = !!chapter.isExisting || !!chapter._id;

    return {
      ...EMPTY_CHAPTER,
      ...chapter,
      audio: isExistingChapter ? null : chapter.audio,
      pdfFile: isExistingChapter ? null : chapter.pdfFile,
      textContent: chapter.textContent || chapter.content || '',
      // ✅ FIX: pdfReady = true when server PDF exists (user-uploaded textFile)
      pdfReady: !!(chapter.textFile || chapter.pdfFile),
      pdfDirty: false,
    };
  };

  const populateEditForm = (story, chapterIndexToEdit = null) => {
    const normalizedChapters = normalizeStoryChapters(story);

    setIsEditing(true);
    setEditId(story._id);
    setApproveOnUpdate(story.status === 'approved');
    setStoryData({
      title: story.title || '',
      description: story.description || '',
      category: story.category?._id || story.category,
      coverImage: null,
      existingCoverImage: story.coverImage || '',
    });
    setChapters(normalizedChapters);

    const shouldEditChapter =
      Number.isInteger(chapterIndexToEdit) &&
      normalizedChapters[chapterIndexToEdit];

    if (shouldEditChapter) {
      setDraft(buildDraftFromChapter(normalizedChapters[chapterIndexToEdit]));
      setEditingChapterIndex(chapterIndexToEdit);
    } else {
      setDraft({ ...EMPTY_CHAPTER });
      setEditingChapterIndex(null);
    }

    setShowModal(true);

    if (shouldEditChapter) {
      setTimeout(() => {
        chapterDraftRef.current?.scrollIntoView({
          behavior: 'smooth',
          block: 'start',
        });
      }, 80);
    }
  };

  const handleEditClick = async (story, chapterIndexToEdit = null) => {
    setViewStory(null);
    setLoading(true);
    try {
      const res = await axios.get(`${API_BASE}/user-stories/${story._id}`, {
        headers,
      });
      populateEditForm(res.data.data || story, chapterIndexToEdit);
    } catch (err) {
      console.error('Story load failed, opening cached row data', err);
      populateEditForm(story, chapterIndexToEdit);
    } finally {
      setLoading(false);
    }
  };

  const handleGeneratePDF = async () => {
    try {
      if (!draft.title.trim()) {
        return alert('❌ Chapter Title is required to generate PDF!');
      }
      if (!draft.textContent.trim()) {
        return alert('❌ Chapter content is required to generate PDF!');
      }

      const pdfFile = await convertTextToPDF(
        sanitizeText(draft.title),
        sanitizeText(draft.textContent),
      );

      setDraft((prev) => ({
        ...prev,
        pdfFile,
        pdfReady: true,
        pdfDirty: false,
      }));

      alert(
        `✅ PDF generated successfully for chapter "${draft.title}"! Now you can add it to the chapter list.`,
      );
    } catch (error) {
      console.error('handleGeneratePDF error:', error);
      alert('❌ PDF generate nahi ho rahi. Console check karo.');
    }
  };

  const resetChapterDraft = () => {
    setDraft({ ...EMPTY_CHAPTER });
    setEditingChapterIndex(null);
  };

  // ─── Bulk helpers ────────────────────────────────────────────────────────────

  const EMPTY_BULK_ROW = {
    title: '',
    description: '',
    image: null,
    audio: null,
    audioFile: null,
    textFile: null,
    textContent: '',
    pdfFile: null,
    pdfReady: false,
    pdfDirty: false,
    isPaid: false,
    diamondPrice: 0,
  };

  const addBulkRow = () => {
    setBulkRows((prev) => [...prev, { ...EMPTY_BULK_ROW }]);
  };

  const handleBulkAudioDrop = (files) => {
    const newRows = Array.from(files).map((file) => ({
      ...EMPTY_BULK_ROW,
      audio: file,
      title: file.name
        .replace(/\.[^.]+$/, '')
        .replace(/[_-]/g, ' ')
        .trim(),
    }));
    setBulkRows((prev) => [...prev, ...newRows]);
  };

  const handleBulkRowChange = (idx, field, value) => {
    setBulkRows((prev) =>
      prev.map((row, i) => (i === idx ? { ...row, [field]: value } : row)),
    );
  };

  const removeBulkRow = (idx) => {
    setBulkRows((prev) => prev.filter((_, i) => i !== idx));
  };

  const handleBulkGeneratePDF = async (idx) => {
    const row = bulkRows[idx];
    if (!row) return;
    if (!row.title.trim()) {
      return alert(`❌ Chapter ${idx + 1}: Title is required to generate PDF!`);
    }
    if (!row.textContent.trim()) {
      return alert(
        `❌ Chapter ${idx + 1}: Content is required to generate PDF!`,
      );
    }
    try {
      const pdfFile = await convertTextToPDF(
        sanitizeText(row.title),
        sanitizeText(row.textContent),
      );
      setBulkRows((prev) =>
        prev.map((r, i) =>
          i === idx
            ? {
                ...r,
                pdfFile,
                pdfReady: true,
                pdfDirty: false,
              }
            : r,
        ),
      );
      alert(`✅ PDF generated successfully for chapter "${row.title}"!`);
    } catch (err) {
      console.error('handleBulkGeneratePDF error:', err);
      alert('❌ PDF generate nahi ho raha. Console check karo.');
    }
  };

  const handleBulkImport = () => {
    if (bulkRows.length === 0) {
      alert('❌ Please add at least one chapter in bulk mode.');
      return;
    }
    for (let i = 0; i < bulkRows.length; i++) {
      const r = bulkRows[i];
      if (!r.title.trim()) {
        alert(`❌ Chapter ${i + 1}: Title is required!`);
        return;
      }
      if (!r.description.trim()) {
        alert(`❌ Chapter ${i + 1}: Summary is required!`);
        return;
      }
      // const hasAudio = !!r.audio || !!r.audioFile;
      // if (!hasAudio) {
      //   alert(`❌ Chapter ${i + 1} ("${r.title}"): Audio (MP3) is required!`);
      //   return;
      // }
      // if (r.pdfDirty && r.textContent?.trim()) {
      if (r.pdfDirty && r.textContent?.trim()) {
        if (!r.pdfFile || !r.pdfReady) {
          alert(
            `❌ Chapter ${i + 1} ("${r.title}"): Content was edited. Please click "Generate PDF" before adding!`,
          );
          return;
        }
      }
    }

    const newChapters = bulkRows.map((row) => {
      const diamondPrice = normalizePrice(row.diamondPrice);
      return {
        ...EMPTY_CHAPTER,
        title: row.title.trim(),
        description: row.description.trim(),
        audio: row.audio,
        audioFile: row.audioFile || null,
        image: row.image || null,
        pdfFile: row.pdfFile || null,
        textFile: row.textFile || null,
        pdfReady: !!(row.pdfFile || row.textFile),
        pdfDirty: false,
        textContent: sanitizeText(row.textContent || ''),
        isPaid: !!row.isPaid && diamondPrice > 0,
        diamondPrice: row.isPaid ? diamondPrice : 0,
      };
    });

    setChapters((prev) => [...prev, ...newChapters]);
    setBulkRows([]);
    setChapterMode('single');
    alert(`✅ ${newChapters.length} chapter(s) added to the story!`);
  };
  // ─────────────────────────────────────────────────────────────────────────────

  const handleEditChapter = (idx) => {
    const chapter = chapters[idx];
    if (!chapter) return;

    setDraft(buildDraftFromChapter(chapter));
    setEditingChapterIndex(idx);

    setTimeout(() => {
      chapterDraftRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
      });
    }, 50);
  };

  // ✅ FIXED validateDraft — allows audio-only update on chapters with existing server PDF
  const validateDraft = () => {
    if (!draft.title.trim()) {
      alert('Chapter Title is required!');
      return false;
    }
    if (!draft.description.trim()) {
      alert('Chapter Summary is required!');
      return false;
    }

    // const hasAudio = !!draft.audio || !!draft.audioFile;
    // if (!hasAudio) {
    //   alert('Chapter Audio (MP3) is required!');
    //   return false;
    // }

    // const isExistingChapter = !!(draft._id || draft.isExisting);
    const isExistingChapter = !!(draft._id || draft.isExisting);

    // PDF already saved on server (user-uploaded OR previously admin-generated)
    const hasServerPdf = !!draft.textFile;

    // Admin just generated a fresh PDF in this session
    const hasNewGeneratedPdf =
      !!draft.pdfFile && draft.pdfReady && !draft.pdfDirty;

    // ✅ Content was modified AND is non-empty → admin must regenerate PDF
    if (draft.pdfDirty && draft.textContent?.trim()) {
      if (!hasNewGeneratedPdf) {
        alert(
          'Chapter content has been modified. Please click "Generate PDF" before saving!',
        );
        return false;
      }
    }

    // ✅ Brand-new chapter (no _id, not existing) with no PDF at all → block
    if (!isExistingChapter && !hasNewGeneratedPdf && !hasServerPdf) {
      alert('Please generate the PDF before adding this chapter!');
      return false;
    }

    return true;
  };

  const handleSaveChapterDraft = () => {
    if (!validateDraft()) return;

    const diamondPrice = normalizePrice(draft.diamondPrice);
    const chapterPayload = {
      ...draft,
      title: draft.title.trim(),
      description: draft.description.trim(),
      textContent: sanitizeText(draft.textContent || ''),
      diamondPrice: draft.isPaid ? diamondPrice : 0,
      isPaid: !!draft.isPaid && diamondPrice > 0,
      pdfDirty: false,
    };

    if (editingChapterIndex !== null) {
      setChapters((prev) =>
        prev.map((chapter, idx) =>
          idx === editingChapterIndex
            ? { ...chapter, ...chapterPayload }
            : chapter,
        ),
      );
      alert('Chapter updated successfully!');
    } else {
      setChapters((prev) => [...prev, chapterPayload]);
    }

    resetChapterDraft();
  };

  const removeChapter = async (idx) => {
    const chapterToRemove = chapters[idx];
    const isSavedChapter = !!(
      chapterToRemove?.isExisting && chapterToRemove?._id
    );

    if (isSavedChapter) {
      if (
        !window.confirm(
          '❗ Move this chapter to trash? It can be restored later.',
        )
      )
        return;
      try {
        await axios.delete(
          `${API_BASE}/stories/${editId}/chapters/${chapterToRemove._id}`,
          { headers },
        );
      } catch (err) {
        console.error(err);
        alert('Chapter delete failed');
        return;
      }
    }

    setChapters((prev) => prev.filter((_, i) => i !== idx));

    if (editingChapterIndex === idx) {
      resetChapterDraft();
      return;
    }
    if (editingChapterIndex !== null && idx < editingChapterIndex) {
      setEditingChapterIndex((prev) => prev - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (chapters.length === 0)
      return alert('❌ Please add at least one chapter!');

    if (editingChapterIndex !== null) {
      return alert(
        'Please update or cancel the chapter edit before saving the story.',
      );
    }
    // const chapterWithoutAudio = chapters.find(
    //   (ch) => !(ch.audio || ch.audioFile),
    // );
    // if (chapterWithoutAudio) {
    //   return alert(
    //     `❌ Chapter "${chapterWithoutAudio.title}" me audio missing hai. Pehle audio add karke chapter update karo.`,
    //   );
    // }
    // setLoading(true);
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append('title', storyData.title);
      formData.append('description', storyData.description);
      formData.append('category', storyData.category);
      if (isEditing) {
        formData.append('status', approveOnUpdate ? 'approved' : 'pending');
      }
      if (storyData.coverImage)
        formData.append('coverImage', storyData.coverImage);

      const audioFiles = [];
      const pdfFiles = [];
      const imageFiles = [];

      const chapterPayload = chapters.map((ch, i) => {
        // ✅ Only push NEW file objects (not strings/paths)
        const audioFileIndex = isUploadFile(ch.audio)
          ? audioFiles.push(ch.audio) - 1
          : -1;
        const textFileIndex = isUploadFile(ch.pdfFile)
          ? pdfFiles.push(ch.pdfFile) - 1
          : -1;
        const imageFileIndex = isUploadFile(ch.image)
          ? imageFiles.push(ch.image) - 1
          : -1;
        const diamondPrice = normalizePrice(ch.diamondPrice);

        return {
          _id: toChapterId(ch._id),
          title: ch.title,
          description: ch.description,
          textContent: ch.textContent || '',
          chapterNumber: i + 1,
          // Preserve existing server paths (strings)
          image: typeof ch.image === 'string' ? ch.image : null,
          audioFile: ch.audioFile || null,
          textFile: ch.textFile || null,
          isAudioPublished: ch.isAudioPublished,
          audioPublishedAt: ch.audioPublishedAt,
          isPaid: !!ch.isPaid && diamondPrice > 0,
          diamondPrice: ch.isPaid ? diamondPrice : 0,
          // ✅ Index-based mapping so backend knows which uploaded file belongs to which chapter
          audioFileIndex,
          textFileIndex,
          imageFileIndex,
        };
      });

      formData.append('chapters', JSON.stringify(chapterPayload));

      // Append actual file objects in order
      audioFiles.forEach((file) => formData.append('chapterAudio', file));
      pdfFiles.forEach((file) => formData.append('chapterTextFile', file));
      imageFiles.forEach((file) => formData.append('chapterImages', file));

      const url = isEditing
        ? `${API_BASE}/stories/${editId}`
        : `${API_BASE}/create-fresh-story`;
      const method = isEditing ? 'patch' : 'post';

      const res = await axios[method](url, formData, {
        headers: { ...headers, 'Content-Type': 'multipart/form-data' },
      });

      if (res.data.success) {
        alert(
          isEditing
            ? '✅ Story updated successfully!' +
                (approveOnUpdate
                  ? ' It is now approved and live.'
                  : ' It is updated but pending approval.')
            : '✅ Story published successfully! It is now pending approval.',
        );
        closeModal();
        fetchInitialData();
      }
    } catch (err) {
      console.error('Story submit failed:', err);

      let userMessage;
      if (!err.response) {
        userMessage =
          'Network error — server tak connect nahi ho paaya. Internet ya server status check karo.';
      } else {
        const status = err.response.status;
        const serverMsg = err.response.data?.message;
        userMessage =
          serverMsg ||
          (status === 413
            ? 'File size too large. Chhota file try karo.'
            : status >= 500
              ? 'Server internal error. Thodi der baad try karo ya server logs check karo.'
              : `Request failed (Status ${status}).`);
      }

      alert('❌ ' + userMessage);
    } finally {
      setLoading(false);
    }
  };

  const closeModal = () => {
    setShowModal(false);
    setIsEditing(false);
    setEditId(null);
    setStoryData({
      title: '',
      description: '',
      category: '',
      coverImage: null,
      existingCoverImage: '',
    });
    setChapters([]);
    setDraft({ ...EMPTY_CHAPTER });
    setEditingChapterIndex(null);
    setApproveOnUpdate(false);
  };

  const filteredStories = useMemo(
    () =>
      allStories.filter((s) =>
        s.title.toLowerCase().includes(searchQuery.toLowerCase()),
      ),
    [allStories, searchQuery],
  );

  return (
    <div style={S.page}>
      <style>{`
        @keyframes storySpin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        @keyframes storyPulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.55; }
        }
      `}</style>
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          marginBottom: '25px',
        }}
      >
        <h2 style={{ color: '#1B254B', margin: 0 }}>📚 Story Management</h2>
        <div style={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
          <label
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
              fontSize: '13px',
              fontWeight: 600,
              color: '#1B254B',
              cursor: 'pointer',
            }}
          >
            <input
              type="checkbox"
              checked={showDeleted}
              onChange={(e) => setShowDeleted(e.target.checked)}
            />
            Show Deleted
          </label>
          <button
            style={S.btnPrimary}
            onClick={() => {
              closeModal();
              setShowModal(true);
            }}
          >
            + New Story
          </button>
        </div>
      </div>

      <div
        style={{
          ...S.card,
          marginBottom: '20px',
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
        }}
      >
        <MdSearch size={24} color="#A3AED0" />
        <input
          type="text"
          placeholder="Search stories..."
          style={{
            border: 'none',
            width: '100%',
            outline: 'none',
            fontSize: '15px',
          }}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div style={S.card}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr
              style={{
                textAlign: 'left',
                color: '#A3AED0',
                fontSize: '12px',
                borderBottom: '1px solid #F4F7FE',
              }}
            >
              {[
                'STORY DETAILS',
                'CATEGORY',
                'CHAPTERS',
                'POPULAR',
                'RECOMMENDED',
                'STATUS',
                'ACTIONS',
              ].map((h) => (
                <th key={h} style={{ padding: '14px 16px' }}>
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredStories.map((s) => (
              <tr key={s._id} style={{ borderBottom: '1px solid #F4F7FE' }}>
                <td style={{ padding: '14px 16px' }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                    }}
                  >
                    <img
                      src={
                        s.coverImage
                          ? getAssetUrl(s.coverImage)
                          : 'https://via.placeholder.com/50'
                      }
                      style={{
                        width: '48px',
                        height: '48px',
                        borderRadius: '10px',
                        objectFit: 'cover',
                      }}
                      alt=""
                    />
                    <div>
                      <div
                        style={{
                          fontWeight: '700',
                          color: '#1B254B',
                          fontSize: '14px',
                        }}
                      >
                        {s.title}
                      </div>
                      <div style={{ fontSize: '11px', color: '#A3AED0' }}>
                        ID: {s._id.slice(0, 8)}…
                      </div>
                    </div>
                  </div>
                </td>
                <td
                  style={{
                    padding: '14px 16px',
                    color: '#707EAE',
                    fontSize: '13px',
                  }}
                >
                  {s.category?.name || 'Uncategorized'}
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <button
                    onClick={() => openViewStory(s)}
                    style={{
                      color: '#4318FF',
                      background: '#F4F7FE',
                      border: 'none',
                      padding: '5px 12px',
                      borderRadius: '8px',
                      cursor: 'pointer',
                      fontWeight: 'bold',
                      fontSize: '12px',
                    }}
                  >
                    {s.chapters?.length || 0} Chapters{' '}
                    <MdVisibility style={{ verticalAlign: 'middle' }} />
                  </button>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <button
                    onClick={() =>
                      handleToggleFlag(s._id, 'isPopular', s.isPopular)
                    }
                    style={{
                      padding: '6px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '700',
                      background: s.isPopular ? '#01B574' : '#CBD5E0',
                      color: '#fff',
                      minWidth: '70px',
                    }}
                  >
                    {s.isPopular ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <button
                    onClick={() =>
                      handleToggleFlag(s._id, 'isRecommended', s.isRecommended)
                    }
                    style={{
                      padding: '6px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '700',
                      background: s.isRecommended ? '#4318FF' : '#CBD5E0',
                      color: '#fff',
                      minWidth: '70px',
                    }}
                  >
                    {s.isRecommended ? 'ON' : 'OFF'}
                  </button>
                </td>
                <td style={{ padding: '14px 16px' }}>
                  <StatusBadge status={s.status} />
                </td>
                <td style={{ padding: '14px 16px' }}>
                  {s.isDeleted ? (
                    <button
                      onClick={() => restoreStory(s._id)}
                      style={{
                        color: '#01B574',
                        background: 'none',
                        border: 'none',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '12px',
                      }}
                    >
                      ♻️ Restore
                    </button>
                  ) : (
                    <>
                      <button
                        onClick={() => handleEditClick(s)}
                        style={{
                          color: '#4318FF',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          marginRight: '14px',
                        }}
                      >
                        <MdEdit size={22} />
                      </button>
                      <button
                        onClick={() => deleteStory(s._id)}
                        style={{
                          color: '#EE5D50',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                      >
                        <MdDelete size={22} />
                      </button>
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filteredStories.length === 0 && (
          <p style={{ textAlign: 'center', color: '#A3AED0', padding: '30px' }}>
            {loading ? 'Loading...' : 'Story not found.'}
          </p>
        )}
      </div>

      {/* View Chapters Modal */}
      {viewStory && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '20px',
              }}
            >
              <h3 style={{ margin: 0 }}>📖 {viewStory.title} — Chapters</h3>
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '16px' }}
              >
                <label
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    fontSize: '12px',
                    fontWeight: 600,
                    color: '#1B254B',
                    cursor: 'pointer',
                  }}
                >
                  <input
                    type="checkbox"
                    checked={showDeletedChapters}
                    onChange={(e) => setShowDeletedChapters(e.target.checked)}
                  />
                  Show Deleted
                </label>
                <MdClose
                  size={28}
                  onClick={() => setViewStory(null)}
                  style={{ cursor: 'pointer' }}
                />
              </div>
            </div>
            {viewStoryLoading && (
              <p style={{ color: '#707EAE', fontSize: '13px' }}>
                Loading chapter details...
              </p>
            )}
            <div style={{ display: 'grid', gap: '14px' }}>
              {(viewStory.chapters || [])
                .filter((ch) => showDeletedChapters || !ch.isDeleted)
                .map((ch, idx) => {
                  const activeIdx = (viewStory.chapters || [])
                    .filter((c) => !c.isDeleted)
                    .findIndex((c) => c._id === ch._id);

                  return (
                    <div
                      key={ch._id || idx}
                      style={{
                        padding: '18px 20px',
                        background: ch.isDeleted ? '#FFF5F5' : '#F8F9FA',
                        borderRadius: '14px',
                        border: ch.isDeleted
                          ? '1px solid #FFCDD2'
                          : '1px solid #E0E5F2',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '12px',
                        }}
                      >
                        <h4 style={{ margin: '0 0 6px 0', color: '#1B254B' }}>
                          {idx + 1}. {ch.title}{' '}
                          {ch.isDeleted && (
                            <span
                              style={{
                                fontSize: '11px',
                                color: '#EE5D50',
                                fontWeight: 'bold',
                              }}
                            >
                              🗑 Deleted
                            </span>
                          )}
                        </h4>
                        <div
                          style={{
                            display: 'flex',
                            gap: '10px',
                            flexWrap: 'wrap',
                            justifyContent: 'flex-end',
                          }}
                        >
                          {ch.audioFile ? (
                            <a
                              href={getAssetUrl(ch.audioFile)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#01B574',
                                textDecoration: 'none',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              🎧 Audio
                            </a>
                          ) : (
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#EE5D50',
                                fontWeight: 'bold',
                              }}
                            >
                              🎧 No Audio
                            </span>
                          )}
                          {ch.textFile ? (
                            <a
                              href={getAssetUrl(ch.textFile)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#4318FF',
                                textDecoration: 'none',
                                fontSize: '12px',
                                fontWeight: 'bold',
                              }}
                            >
                              📄 PDF
                            </a>
                          ) : (
                            <span
                              style={{
                                fontSize: '12px',
                                color: '#EE5D50',
                                fontWeight: 'bold',
                              }}
                            >
                              📄 No PDF
                            </span>
                          )}

                          {ch.isDeleted ? (
                            <button
                              type="button"
                              onClick={() =>
                                restoreChapter(viewStory._id, ch._id)
                              }
                              style={{
                                color: '#01B574',
                                background: 'transparent',
                                border: 'none',
                                cursor: 'pointer',
                                fontSize: '12px',
                                fontWeight: 'bold',
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '4px',
                                padding: 0,
                              }}
                            >
                              ♻️ Restore
                            </button>
                          ) : (
                            <>
                              <button
                                type="button"
                                onClick={() =>
                                  handleEditClick(viewStory, activeIdx)
                                }
                                style={{
                                  color: '#4318FF',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: 0,
                                }}
                              >
                                <MdEdit size={15} />
                                Edit
                              </button>
                              <button
                                type="button"
                                onClick={() =>
                                  deleteChapterFromView(viewStory._id, ch._id)
                                }
                                style={{
                                  color: '#EE5D50',
                                  background: 'transparent',
                                  border: 'none',
                                  cursor: 'pointer',
                                  fontSize: '12px',
                                  fontWeight: 'bold',
                                  display: 'inline-flex',
                                  alignItems: 'center',
                                  gap: '4px',
                                  padding: 0,
                                }}
                              >
                                <MdDelete size={15} />
                                Delete
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                      <p
                        style={{
                          fontSize: '13px',
                          color: '#707EAE',
                          margin: '0 0 8px 0',
                        }}
                      >
                        {ch.description}
                      </p>
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          gap: '8px',
                          fontSize: '11px',
                          color: '#707EAE',
                        }}
                      >
                        {ch.isPaid && normalizePrice(ch.diamondPrice) > 0 ? (
                          <span
                            style={{ color: '#4318FF', fontWeight: 'bold' }}
                          >
                            💎 {normalizePrice(ch.diamondPrice)} Diamonds
                          </span>
                        ) : (
                          <span style={{ fontWeight: 'bold' }}>
                            Free Chapter
                          </span>
                        )}
                        {ch.isAudioPublished && (
                          <span
                            style={{ color: '#01B574', fontWeight: 'bold' }}
                          >
                            Audio Published
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
            </div>
          </div>
        </div>
      )}

      {/* Create / Edit Modal */}
      {showModal && (
        <div style={S.modal}>
          <div style={S.modalBox}>
            <button
              onClick={closeModal}
              style={{
                position: 'absolute',
                top: '20px',
                right: '20px',
                border: 'none',
                background: 'none',
                cursor: 'pointer',
              }}
            >
              <MdClose size={28} />
            </button>

            <h2 style={{ marginBottom: '24px', color: '#1B254B' }}>
              {isEditing ? '✏️ Story Edit ' : '🚀 New Story  '}
            </h2>

            <form onSubmit={handleSubmit}>
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: '20px',
                  marginBottom: '4px',
                }}
              >
                <div>
                  <Label>Story Title</Label>
                  <input
                    style={S.input}
                    type="text"
                    placeholder="Horror Night..."
                    value={storyData.title}
                    onChange={(e) =>
                      setStoryData({ ...storyData, title: e.target.value })
                    }
                    required
                  />

                  <Label>Category</Label>
                  <select
                    style={S.input}
                    value={storyData.category}
                    onChange={(e) =>
                      setStoryData({ ...storyData, category: e.target.value })
                    }
                    required
                  >
                    <option value="">Select Category</option>
                    {categories.map((c) => (
                      <option key={c._id} value={c._id}>
                        {c.name}
                      </option>
                    ))}
                  </select>

                  <Label>
                    Cover Image{' '}
                    {isEditing && (
                      <span style={{ color: '#01B574', fontWeight: 'normal' }}>
                        (Leave empty to keep as is)
                      </span>
                    )}
                  </Label>
                  <input
                    style={S.input}
                    type="file"
                    accept="image/*"
                    onChange={(e) =>
                      setStoryData({
                        ...storyData,
                        coverImage: e.target.files?.[0] || null,
                      })
                    }
                  />
                  {storyData.existingCoverImage && !storyData.coverImage && (
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                        marginTop: '-6px',
                        marginBottom: '12px',
                        color: '#707EAE',
                        fontSize: '12px',
                      }}
                    >
                      <img
                        src={getAssetUrl(storyData.existingCoverImage)}
                        alt="Current cover"
                        style={{
                          width: '42px',
                          height: '42px',
                          borderRadius: '8px',
                          objectFit: 'cover',
                        }}
                      />
                      <span>Existing cover image will be kept</span>
                    </div>
                  )}
                </div>
                <div>
                  <Label>Story Description</Label>
                  <textarea
                    style={{ ...S.input, height: '155px', resize: 'vertical' }}
                    placeholder="Write The content of the story in brief..."
                    value={storyData.description}
                    onChange={(e) =>
                      setStoryData({
                        ...storyData,
                        description: e.target.value,
                      })
                    }
                    required
                  />
                </div>
              </div>

              {/* Chapter Draft Section */}
              <div
                ref={chapterDraftRef}
                style={{
                  background: '#F4F7FE',
                  padding: '20px',
                  borderRadius: '20px',
                  border: '2px dashed #4318FF',
                  marginBottom: '20px',
                }}
              >
                {/* Tab toggle: Single vs Bulk */}
                <div
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    marginBottom: '16px',
                  }}
                >
                  <p
                    style={{
                      fontWeight: '700',
                      color: '#4318FF',
                      margin: 0,
                      fontSize: '15px',
                    }}
                  >
                    {chapterMode === 'bulk'
                      ? '📦 Bulk Chapter Import'
                      : editingChapterIndex !== null
                        ? '✏️ Edit Chapter'
                        : '➕ Chapter Draft'}
                  </p>
                  {editingChapterIndex === null && (
                    <div
                      style={{
                        display: 'flex',
                        gap: '6px',
                        background: '#E8EDFC',
                        borderRadius: '10px',
                        padding: '4px',
                      }}
                    >
                      <button
                        type="button"
                        onClick={() => setChapterMode('single')}
                        style={{
                          ...S.bulkTab,
                          background:
                            chapterMode === 'single'
                              ? '#4318FF'
                              : 'transparent',
                          color: chapterMode === 'single' ? '#fff' : '#4318FF',
                        }}
                      >
                        Single
                      </button>
                      <button
                        type="button"
                        onClick={() => setChapterMode('bulk')}
                        style={{
                          ...S.bulkTab,
                          background:
                            chapterMode === 'bulk' ? '#4318FF' : 'transparent',
                          color: chapterMode === 'bulk' ? '#fff' : '#4318FF',
                        }}
                      >
                        📦 Bulk
                      </button>
                    </div>
                  )}
                </div>

                {/* ── BULK PANEL ── */}
                {chapterMode === 'bulk' && editingChapterIndex === null && (
                  <div>
                    {/* Top Action Bar */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: '10px',
                        marginBottom: '16px',
                        background: '#fff',
                        padding: '14px 18px',
                        borderRadius: '14px',
                        border: '1px solid #E0E5F2',
                      }}
                    >
                      <div>
                        <span
                          style={{
                            fontWeight: 700,
                            color: '#1B254B',
                            fontSize: '14px',
                          }}
                        >
                          📦 Bulk Chapters ({bulkRows.length})
                        </span>
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#707EAE',
                            marginTop: '2px',
                          }}
                        >
                          Add multiple full chapter forms or choose audio files
                          to auto-populate.
                        </div>
                      </div>
                      <div
                        style={{
                          display: 'flex',
                          gap: '8px',
                          flexWrap: 'wrap',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() =>
                            document.getElementById('bulkAudioPicker').click()
                          }
                          style={{
                            ...S.btnSecondary,
                            fontSize: '12px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          🎵 Auto-populate from Audio Files
                        </button>
                        <input
                          id="bulkAudioPicker"
                          type="file"
                          accept=".mp3,audio/*"
                          multiple
                          style={{ display: 'none' }}
                          onChange={(e) => handleBulkAudioDrop(e.target.files)}
                        />
                        <button
                          type="button"
                          onClick={addBulkRow}
                          style={{
                            ...S.btnPrimary,
                            fontSize: '12px',
                            padding: '8px 16px',
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          ➕ Add Chapter Form
                        </button>
                      </div>
                    </div>

                    {/* Empty State */}
                    {bulkRows.length === 0 && (
                      <div
                        style={{
                          background: '#fff',
                          border: '2px dashed #A3AED0',
                          borderRadius: '16px',
                          padding: '36px 20px',
                          textAlign: 'center',
                          marginBottom: '16px',
                        }}
                      >
                        <div style={{ fontSize: '38px', marginBottom: '8px' }}>
                          📚
                        </div>
                        <div
                          style={{
                            fontWeight: 700,
                            color: '#1B254B',
                            fontSize: '15px',
                            marginBottom: '4px',
                          }}
                        >
                          No Bulk Chapters Added Yet
                        </div>
                        <div
                          style={{
                            fontSize: '13px',
                            color: '#707EAE',
                            marginBottom: '16px',
                          }}
                        >
                          Click "+ Add Chapter Form" to fill chapter details
                          manually, or select multiple audio files.
                        </div>
                        <div
                          style={{
                            display: 'flex',
                            justifyContent: 'center',
                            gap: '10px',
                          }}
                        >
                          <button
                            type="button"
                            onClick={addBulkRow}
                            style={S.btnPrimary}
                          >
                            ➕ Add Chapter Form
                          </button>
                          <button
                            type="button"
                            onClick={() =>
                              document.getElementById('bulkAudioPicker').click()
                            }
                            style={S.btnSecondary}
                          >
                            🎵 Select Multiple Audio Files
                          </button>
                        </div>
                      </div>
                    )}

                    {/* Full Chapter Forms for Each Bulk Row */}
                    {bulkRows.map((row, idx) => (
                      <div
                        key={idx}
                        style={{
                          background: '#fff',
                          borderRadius: '16px',
                          border: '1.5px solid #CBD8F6',
                          padding: '20px',
                          marginBottom: '20px',
                          boxShadow: '0 8px 24px rgba(67, 24, 255, 0.05)',
                        }}
                      >
                        {/* Header for this chapter */}
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            marginBottom: '16px',
                            paddingBottom: '12px',
                            borderBottom: '1px solid #EEF2F6',
                          }}
                        >
                          <div
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: '10px',
                            }}
                          >
                            <span
                              style={{
                                background: '#4318FF',
                                color: '#fff',
                                borderRadius: '50%',
                                width: '28px',
                                height: '28px',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '13px',
                                fontWeight: 700,
                                flexShrink: 0,
                              }}
                            >
                              {idx + 1}
                            </span>
                            <span
                              style={{
                                fontWeight: 700,
                                color: '#1B254B',
                                fontSize: '15px',
                              }}
                            >
                              Chapter {idx + 1}:{' '}
                              {row.title || 'Untitled Chapter'}
                            </span>
                          </div>
                          <button
                            type="button"
                            onClick={() => removeBulkRow(idx)}
                            style={{
                              background: '#FFF0EF',
                              border: '1px solid #FFDCD9',
                              borderRadius: '8px',
                              cursor: 'pointer',
                              color: '#EE5D50',
                              padding: '6px 12px',
                              fontSize: '12px',
                              fontWeight: 700,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '4px',
                            }}
                          >
                            <MdDelete size={16} /> Remove
                          </button>
                        </div>

                        {/* 2-Column Full Form (Identical to Single Mode) */}
                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '18px',
                          }}
                        >
                          {/* LEFT COLUMN */}
                          <div>
                            <Label>Chapter Title *</Label>
                            <input
                              style={S.input}
                              type="text"
                              placeholder={`Chapter ${idx + 1}: The Beginning...`}
                              value={row.title}
                              onChange={(e) => {
                                const newTitle = e.target.value;
                                handleBulkRowChange(idx, 'title', newTitle);
                              }}
                            />

                            <Label>Chapter Summary *</Label>
                            <textarea
                              style={{
                                ...S.input,
                                height: '75px',
                                resize: 'vertical',
                              }}
                              placeholder="Is chapter mein kya hota hai..."
                              value={row.description}
                              onChange={(e) =>
                                handleBulkRowChange(
                                  idx,
                                  'description',
                                  e.target.value,
                                )
                              }
                            />

                            <div
                              style={{
                                display: 'grid',
                                gridTemplateColumns: '1fr 1fr',
                                gap: '10px',
                              }}
                            >
                              <div>
                                <Label>Thumbnail (optional)</Label>
                                <input
                                  type="file"
                                  accept="image/*"
                                  style={{
                                    width: '100%',
                                    marginBottom: '14px',
                                    fontSize: '12px',
                                  }}
                                  onChange={(e) =>
                                    handleBulkRowChange(
                                      idx,
                                      'image',
                                      e.target.files[0] || null,
                                    )
                                  }
                                />
                                {typeof row.image === 'string' && (
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#707EAE',
                                    }}
                                  >
                                    Existing thumbnail selected
                                  </div>
                                )}
                              </div>

                              <div>
                                <Label>
                                  Audio (MP3) *{' '}
                                  {row.audioFile && !row.audio && (
                                    <span
                                      style={{
                                        color: '#01B574',
                                        fontWeight: 'normal',
                                        textTransform: 'none',
                                      }}
                                    >
                                      ✅ Saved
                                    </span>
                                  )}
                                </Label>

                                <input
                                  type="file"
                                  accept=".mp3,audio/*"
                                  style={{
                                    width: '100%',
                                    marginBottom: '14px',
                                    fontSize: '12px',
                                  }}
                                  onChange={(e) =>
                                    handleBulkRowChange(
                                      idx,
                                      'audio',
                                      e.target.files[0] || null,
                                    )
                                  }
                                />
                                {row.audio && (
                                  <div
                                    style={{
                                      fontSize: '11px',
                                      color: '#01B574',
                                      fontWeight: 700,
                                      marginTop: '4px',
                                    }}
                                  >
                                    🎵 Selected audio: {row.audio.name}
                                  </div>
                                )}
                              </div>
                            </div>

                            {/* Audio Mixer for this chapter */}
                            <div style={{ marginTop: '10px' }}>
                              <AudioMixer
                                title={`Generate Mixed Audio (Chapter ${idx + 1})`}
                                onMixedFile={(file) =>
                                  handleBulkRowChange(idx, 'audio', file)
                                }
                              />
                              <div
                                style={{
                                  marginTop: '8px',
                                  background: '#EFF6FF',
                                  border: '1px solid #BFDBFE',
                                  borderRadius: '8px',
                                  padding: '8px 12px',
                                  fontSize: '11px',
                                  color: '#1D4ED8',
                                  lineHeight: '1.4',
                                }}
                              >
                                💡 <strong>Note:</strong> Generating mixed audio
                                automatically attaches it as the audio for
                                Chapter {idx + 1}. You can also download it for
                                your records.
                              </div>
                            </div>
                          </div>

                          {/* RIGHT COLUMN */}
                          <div>
                            {/* Diamond Price Section */}
                            <div
                              style={{
                                width: '100%',
                                background: '#f8f9ff',
                                border: '1px solid #dbe2ff',
                                borderRadius: '16px',
                                padding: '16px',
                                marginBottom: '16px',
                                boxSizing: 'border-box',
                              }}
                            >
                              <Label
                                style={{
                                  display: 'block',
                                  marginBottom: '8px',
                                  fontSize: '13px',
                                  fontWeight: '700',
                                  color: '#1B254B',
                                }}
                              >
                                Diamond Price 💎
                              </Label>
                              <input
                                style={{
                                  width: '100%',
                                  padding: '12px 14px',
                                  borderRadius: '10px',
                                  border: '1px solid #cfd8ff',
                                  outline: 'none',
                                  fontSize: '14px',
                                  background: '#fff',
                                  marginBottom: '12px',
                                  color: '#1B254B',
                                  boxSizing: 'border-box',
                                }}
                                type="number"
                                min="0"
                                placeholder="Enter diamonds required"
                                value={row.diamondPrice || ''}
                                onChange={(e) => {
                                  const nextPrice =
                                    e.target.value === ''
                                      ? ''
                                      : Number(e.target.value);
                                  handleBulkRowChange(
                                    idx,
                                    'diamondPrice',
                                    nextPrice,
                                  );
                                  if (Number(nextPrice) > 0) {
                                    handleBulkRowChange(idx, 'isPaid', true);
                                  }
                                }}
                              />
                              <label
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '10px',
                                  cursor: 'pointer',
                                  userSelect: 'none',
                                }}
                              >
                                <input
                                  type="checkbox"
                                  checked={row.isPaid}
                                  onChange={(e) => {
                                    handleBulkRowChange(
                                      idx,
                                      'isPaid',
                                      e.target.checked,
                                    );
                                    if (!e.target.checked) {
                                      handleBulkRowChange(
                                        idx,
                                        'diamondPrice',
                                        0,
                                      );
                                    }
                                  }}
                                  style={{
                                    width: '16px',
                                    height: '16px',
                                    cursor: 'pointer',
                                  }}
                                />
                                <span
                                  style={{
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    color: '#1B254B',
                                  }}
                                >
                                  Paid Chapter
                                </span>
                              </label>
                            </div>

                            {/* Chapter Content & PDF Generation */}
                            <Label>
                              Chapter Content{' '}
                              {row.textFile && !row.pdfDirty && (
                                <span
                                  style={{
                                    color: '#01B574',
                                    fontWeight: 'bold',
                                    fontSize: '11px',
                                  }}
                                >
                                  ✅ PDF on server
                                </span>
                              )}
                              {row.pdfFile && row.pdfReady && !row.pdfDirty && (
                                <span
                                  style={{
                                    color: '#01B574',
                                    fontWeight: 'bold',
                                    fontSize: '11px',
                                  }}
                                >
                                  ✅ New PDF ready
                                </span>
                              )}
                              {row.pdfDirty && row.textContent?.trim() && (
                                <span
                                  style={{
                                    color: '#EE5D50',
                                    fontWeight: 'bold',
                                    fontSize: '11px',
                                  }}
                                >
                                  ⚠️ Regenerate PDF!
                                </span>
                              )}
                            </Label>
                            <textarea
                              style={{
                                ...S.input,
                                height: '165px',
                                resize: 'vertical',
                                fontFamily: 'serif',
                                fontSize: '13px',
                                lineHeight: '1.6',
                              }}
                              placeholder={`Write the complete content of Chapter ${idx + 1} here...\n\nAfter writing the content, click "Generate PDF".`}
                              value={row.textContent}
                              onChange={(e) => {
                                const newContent = e.target.value;
                                handleBulkRowChange(
                                  idx,
                                  'textContent',
                                  newContent,
                                );
                                handleBulkRowChange(
                                  idx,
                                  'pdfReady',
                                  newContent.trim() ? false : !!row.textFile,
                                );
                                handleBulkRowChange(
                                  idx,
                                  'pdfDirty',
                                  !!newContent.trim(),
                                );
                              }}
                            />

                            {/* PDF Generation Button */}
                            <button
                              type="button"
                              onClick={() => handleBulkGeneratePDF(idx)}
                              style={{
                                ...S.btnPDF,
                                width: '100%',
                                justifyContent: 'center',
                                marginBottom: '0',
                                opacity: row.textContent.trim() ? 1 : 0.5,
                              }}
                            >
                              <MdArticle size={18} />
                              {row.pdfFile && row.pdfReady
                                ? '🔄 PDF Regenerate Karen'
                                : '📄 Generate PDF'}
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}

                    {/* Bottom Actions Bar */}
                    {bulkRows.length > 0 && (
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          marginTop: '16px',
                          background: '#fff',
                          padding: '14px 18px',
                          borderRadius: '14px',
                          border: '1px solid #E0E5F2',
                        }}
                      >
                        <button
                          type="button"
                          onClick={addBulkRow}
                          style={{
                            ...S.btnSecondary,
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: '6px',
                          }}
                        >
                          ➕ Add Another Chapter
                        </button>
                        <div style={{ display: 'flex', gap: '10px' }}>
                          <button
                            type="button"
                            onClick={() => {
                              setBulkRows([]);
                              setChapterMode('single');
                            }}
                            style={S.btnSecondary}
                          >
                            Cancel
                          </button>
                          <button
                            type="button"
                            onClick={handleBulkImport}
                            style={{
                              ...S.btnPrimary,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: '8px',
                            }}
                          >
                            📦 Add All {bulkRows.length} Chapter
                            {bulkRows.length !== 1 ? 's' : ''} to Story
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {/* ── SINGLE CHAPTER PANEL ── */}
                {(chapterMode === 'single' || editingChapterIndex !== null) && (
                  <div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr',
                        gap: '14px',
                      }}
                    >
                      <div>
                        <Label>Chapter Title *</Label>
                        <input
                          style={S.input}
                          type="text"
                          placeholder="Chapter 1: The Beginning..."
                          value={draft.title}
                          onChange={(e) => {
                            const newTitle = e.target.value;
                            setDraft((prev) => ({
                              ...prev,
                              title: newTitle,
                              // ✅ FIX: only set pdfDirty from title change if content exists
                              pdfReady:
                                !prev.textContent.trim() && !!prev.textFile
                                  ? true
                                  : prev.pdfReady,
                              pdfDirty: prev.textContent.trim()
                                ? prev.pdfDirty
                                : false,
                            }));
                          }}
                        />

                        <Label>Chapter Summary *</Label>
                        <textarea
                          style={{
                            ...S.input,
                            height: '75px',
                            resize: 'vertical',
                          }}
                          placeholder="Is chapter mein kya hota hai..."
                          value={draft.description}
                          onChange={(e) =>
                            setDraft({ ...draft, description: e.target.value })
                          }
                        />

                        <div
                          style={{
                            display: 'grid',
                            gridTemplateColumns: '1fr 1fr',
                            gap: '10px',
                          }}
                        >
                          <div>
                            <Label>Thumbnail (optional)</Label>
                            <input
                              type="file"
                              accept="image/*"
                              style={{
                                width: '100%',
                                marginBottom: '14px',
                                fontSize: '12px',
                              }}
                              onChange={(e) =>
                                setDraft({ ...draft, image: e.target.files[0] })
                              }
                            />
                            {typeof draft.image === 'string' && (
                              <div
                                style={{ fontSize: '11px', color: '#707EAE' }}
                              >
                                Existing thumbnail selected
                              </div>
                            )}
                          </div>
                          <div>
                            <Label>
                              Audio (MP3) *{' '}
                              {/* ✅ Show existing audio status clearly */}
                              {draft.audioFile && !draft.audio && (
                                <span
                                  style={{
                                    color: '#01B574',
                                    fontWeight: 'normal',
                                    textTransform: 'none',
                                  }}
                                >
                                  ✅ Saved
                                </span>
                              )}
                            </Label>

                            <input
                              type="file"
                              accept=".mp3,audio/*"
                              style={{
                                width: '100%',
                                marginBottom: '14px',
                                fontSize: '12px',
                              }}
                              onChange={(e) =>
                                setDraft({ ...draft, audio: e.target.files[0] })
                              }
                            />
                            {draft.audio && (
                              <div
                                style={{
                                  fontSize: '11px',
                                  color: '#01B574',
                                  fontWeight: 700,
                                  marginTop: '8px',
                                }}
                              >
                                Selected audio: {draft.audio.name}
                              </div>
                            )}
                            {draft.audioFile && !draft.audio && (
                              <div
                                style={{ fontSize: '11px', color: '#707EAE' }}
                              >
                                🎧{' '}
                                <a
                                  href={getAssetUrl(draft.audioFile)}
                                  target="_blank"
                                  rel="noreferrer"
                                  style={{ color: '#4318FF' }}
                                >
                                  Listen to existing audio
                                </a>{' '}
                                — upload new to replace
                              </div>
                            )}
                          </div>
                        </div>

                        <div style={{ marginTop: '10px' }}>
                          <AudioMixer
                            title="Generate Mixed Chapter Audio"
                            onMixedFile={(file) =>
                              setDraft((prev) => ({ ...prev, audio: file }))
                            }
                          />
                          <div
                            style={{
                              marginTop: '8px',
                              background: '#EFF6FF',
                              border: '1px solid #BFDBFE',
                              borderRadius: '8px',
                              padding: '8px 12px',
                              fontSize: '11px',
                              color: '#1D4ED8',
                              lineHeight: '1.4',
                            }}
                          >
                            💡 <strong>Note:</strong> Generating mixed audio
                            automatically attaches it to this chapter draft. You
                            can also download it if needed.
                          </div>
                        </div>
                      </div>

                      {/* Diamond Price Section */}
                      <div
                        style={{
                          width: '100%',
                          background: '#f8f9ff',
                          border: '1px solid #dbe2ff',
                          borderRadius: '16px',
                          padding: '20px',
                          marginTop: '16px',
                          marginBottom: '18px',
                          boxSizing: 'border-box',
                        }}
                      >
                        <Label
                          style={{
                            display: 'block',
                            marginBottom: '10px',
                            fontSize: '14px',
                            fontWeight: '700',
                            color: '#1B254B',
                          }}
                        >
                          Diamond Price 💎
                        </Label>
                        <input
                          style={{
                            width: '100%',
                            padding: '14px 16px',
                            borderRadius: '12px',
                            border: '1px solid #cfd8ff',
                            outline: 'none',
                            fontSize: '15px',
                            background: '#fff',
                            marginBottom: '16px',
                            color: '#1B254B',
                            boxSizing: 'border-box',
                          }}
                          type="number"
                          min="0"
                          placeholder="Enter diamonds required"
                          value={draft.diamondPrice || ''}
                          onChange={(e) => {
                            const nextPrice =
                              e.target.value === ''
                                ? ''
                                : Number(e.target.value);
                            setDraft({
                              ...draft,
                              diamondPrice: nextPrice,
                              isPaid:
                                Number(nextPrice) > 0 ? true : draft.isPaid,
                            });
                          }}
                        />
                        <label
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            cursor: 'pointer',
                            userSelect: 'none',
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={draft.isPaid}
                            onChange={(e) =>
                              setDraft({
                                ...draft,
                                isPaid: e.target.checked,
                                diamondPrice: e.target.checked
                                  ? draft.diamondPrice
                                  : 0,
                              })
                            }
                            style={{
                              width: '18px',
                              height: '18px',
                              cursor: 'pointer',
                            }}
                          />
                          <span
                            style={{
                              fontSize: '14px',
                              fontWeight: '600',
                              color: '#1B254B',
                            }}
                          >
                            Paid Chapter
                          </span>
                        </label>
                      </div>

                      {/* Text Content Area */}
                      <div>
                        <Label>
                          Chapter Content {/* ✅ Show clear PDF status badge */}
                          {draft.textFile && !draft.pdfDirty && (
                            <span
                              style={{
                                color: '#01B574',
                                fontWeight: 'bold',
                                fontSize: '11px',
                              }}
                            >
                              ✅ User PDF on server
                            </span>
                          )}
                          {draft.pdfFile &&
                            draft.pdfReady &&
                            !draft.pdfDirty && (
                              <span
                                style={{
                                  color: '#01B574',
                                  fontWeight: 'bold',
                                  fontSize: '11px',
                                }}
                              >
                                ✅ New PDF ready
                              </span>
                            )}
                          {draft.pdfDirty && draft.textContent?.trim() && (
                            <span
                              style={{
                                color: '#EE5D50',
                                fontWeight: 'bold',
                                fontSize: '11px',
                              }}
                            >
                              ⚠️ Regenerate PDF!
                            </span>
                          )}
                        </Label>
                        <textarea
                          style={{
                            ...S.input,
                            height: '215px',
                            resize: 'vertical',
                            fontFamily: 'serif',
                            fontSize: '13px',
                            lineHeight: '1.6',
                          }}
                          placeholder={
                            draft.textFile && !draft.textContent
                              ? '📄 This chapter already has a PDF on the server. You can add audio without touching this field.\n\nTo replace the PDF: type new content here then click "Generate PDF".'
                              : `Write the complete content of this chapter here...\n\nOnce upon a time, Ramu saw a lioness in the jungle...\n\nAfter writing the content, click "Generate PDF".`
                          }
                          value={draft.textContent}
                          onChange={(e) => {
                            const newContent = e.target.value;
                            setDraft((prev) => ({
                              ...prev,
                              textContent: newContent,
                              // ✅ FIX: Only mark dirty when there's actual content.
                              // If admin clears the field back to empty, treat as not dirty.
                              pdfReady: newContent.trim()
                                ? false
                                : !!prev.textFile,
                              pdfDirty: !!newContent.trim(),
                            }));
                          }}
                        />

                        {/* PDF Generation Button */}
                        <button
                          type="button"
                          onClick={handleGeneratePDF}
                          style={{
                            ...S.btnPDF,
                            width: '100%',
                            justifyContent: 'center',
                            marginBottom: '0',
                            opacity: draft.textContent.trim() ? 1 : 0.5,
                          }}
                        >
                          <MdArticle size={18} />
                          {draft.pdfFile && draft.pdfReady
                            ? '🔄 PDF Regenerate Karen'
                            : '📄 Generate PDF'}
                        </button>

                        {/* ✅ Show server PDF info clearly */}
                        {draft.textFile && !draft.pdfFile && (
                          <div
                            style={{
                              marginTop: '8px',
                              fontSize: '11px',
                              color: '#707EAE',
                            }}
                          >
                            📄 Existing PDF:{' '}
                            <a
                              href={getAssetUrl(draft.textFile)}
                              target="_blank"
                              rel="noreferrer"
                              style={{ color: '#4318FF' }}
                            >
                              View PDF
                            </a>{' '}
                            — will be kept unless you type content and
                            regenerate.
                          </div>
                        )}
                      </div>
                    </div>

                    {/* Add to List */}
                    <div
                      style={{
                        marginTop: '14px',
                        display: 'flex',
                        gap: '10px',
                        justifyContent: 'flex-end',
                      }}
                    >
                      {editingChapterIndex !== null && (
                        <button
                          type="button"
                          onClick={resetChapterDraft}
                          style={S.btnSecondary}
                        >
                          Cancel Edit
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={handleSaveChapterDraft}
                        style={{
                          ...S.btnGreen,
                          // ✅ FIX: Button is enabled when:
                          // - has audio AND (has server PDF OR has new generated PDF)
                          // - For new chapters: need a PDF
                          opacity:
                            (draft.audio || draft.audioFile) &&
                            (draft.textFile ||
                              (draft.pdfFile &&
                                draft.pdfReady &&
                                !draft.pdfDirty))
                              ? 1
                              : 0.6,
                        }}
                      >
                        {editingChapterIndex !== null
                          ? 'Update Chapter'
                          : 'Submit Chapter'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Chapters List */}
              <div style={{ marginBottom: '24px' }}>
                <p
                  style={{
                    fontWeight: '700',
                    color: '#1B254B',
                    marginBottom: '10px',
                    fontSize: '14px',
                  }}
                >
                  Added Chapters ({chapters.length}):
                </p>
                {chapters.length === 0 && (
                  <p
                    style={{
                      color: '#A3AED0',
                      fontSize: '13px',
                      textAlign: 'center',
                      padding: '14px',
                      background: '#F8F9FA',
                      borderRadius: '12px',
                    }}
                  >
                    No chapters have been added yet. Please fill in the chapter
                    details above and click the "Submit Chapter" button.
                  </p>
                )}
                {chapters.map((ch, i) => (
                  <div key={i} style={S.chapterRow}>
                    <div
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '10px',
                      }}
                    >
                      <span
                        style={{
                          background: '#4318FF',
                          color: '#fff',
                          borderRadius: '50%',
                          width: '26px',
                          height: '26px',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          flexShrink: 0,
                        }}
                      >
                        {i + 1}
                      </span>
                      <div>
                        <span style={{ fontWeight: '600', color: '#1B254B' }}>
                          {ch.title}
                        </span>
                        {ch.isExisting && (
                          <span
                            style={{
                              marginLeft: '8px',
                              fontSize: '11px',
                              color: '#01B574',
                              fontWeight: 'bold',
                            }}
                          >
                            (Existing)
                          </span>
                        )}
                        <div
                          style={{
                            fontSize: '11px',
                            color: '#4318FF',
                            fontWeight: 'bold',
                            marginTop: '4px',
                          }}
                        >
                          {ch.isPaid && normalizePrice(ch.diamondPrice) > 0
                            ? `💎 ${normalizePrice(ch.diamondPrice)} Diamonds`
                            : 'Free Chapter'}
                          {(ch.audio || ch.audioFile) && ' 🎵 Audio '}
                          {(ch.pdfFile || ch.textFile) && '📄 PDF '}
                          {ch.image && ' 🖼 Thumbnail'}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        gap: '8px',
                        flexShrink: 0,
                        alignItems: 'center',
                      }}
                    >
                      {(ch.audioFile || ch.textFile) && (
                        <div
                          style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '4px',
                            marginRight: '6px',
                            fontSize: '10px',
                            fontWeight: 'bold',
                          }}
                        >
                          {ch.audioFile && (
                            <a
                              href={getAssetUrl(ch.audioFile)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#01B574',
                                textDecoration: 'none',
                              }}
                            >
                              🎧 Audio
                            </a>
                          )}
                          {ch.textFile && (
                            <a
                              href={getAssetUrl(ch.textFile)}
                              target="_blank"
                              rel="noreferrer"
                              style={{
                                color: '#4318FF',
                                textDecoration: 'none',
                              }}
                            >
                              📄 PDF
                            </a>
                          )}
                        </div>
                      )}
                      <button
                        type="button"
                        onClick={() => handleEditChapter(i)}
                        style={{
                          color: '#4318FF',
                          background: '#F4F7FE',
                          border: '1px solid #E0E5F2',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          padding: '6px 10px',
                          fontSize: '11px',
                          fontWeight: '700',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '4px',
                        }}
                      >
                        <MdEdit size={16} />
                        Edit Chapter
                      </button>
                      <button
                        type="button"
                        onClick={() => removeChapter(i)}
                        style={{
                          color: '#EE5D50',
                          background: 'none',
                          border: 'none',
                          cursor: 'pointer',
                          padding: 0,
                        }}
                      >
                        <MdDelete size={22} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>

              {isEditing && (
                <div
                  style={{
                    background: approveOnUpdate ? '#01B57415' : '#F4F7FE',
                    border: `1px solid ${approveOnUpdate ? '#01B574' : '#E0E5F2'}`,
                    padding: '14px 16px',
                    borderRadius: '12px',
                    marginBottom: '16px',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontWeight: '700',
                        color: '#1B254B',
                        fontSize: '14px',
                      }}
                    >
                      Approve story on update
                    </div>
                    <div style={{ fontSize: '12px', color: '#707EAE' }}>
                      If ON, the story will be approved immediately after
                      update.
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => setApproveOnUpdate(!approveOnUpdate)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '999px',
                      border: 'none',
                      cursor: 'pointer',
                      fontWeight: '700',
                      background: approveOnUpdate ? '#01B574' : '#CBD5E0',
                      color: '#fff',
                    }}
                  >
                    {approveOnUpdate ? 'ON' : 'OFF'}
                  </button>
                </div>
              )}

              {!isEditing && (
                <div
                  style={{
                    background: '#FFF3CD',
                    border: '1px solid #FFE082',
                    borderRadius: '12px',
                    padding: '12px 16px',
                    marginBottom: '16px',
                    fontSize: '13px',
                    color: '#856404',
                  }}
                >
                  ℹ️ <strong>Note:</strong> New story will be saved in{' '}
                  <strong>PENDING</strong> status.
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  ...S.btnPrimary,
                  width: '100%',
                  fontSize: '16px',
                  padding: '14px',
                  opacity: loading ? 0.85 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '10px',
                  cursor: loading ? 'not-allowed' : 'pointer',
                }}
              >
                {loading ? (
                  <>
                    <StoryLoaderSpinner />
                    <span
                      style={{
                        animation: 'storyPulse 1.4s ease-in-out infinite',
                      }}
                    >
                      Processing... {formatLoaderTime(loadingSeconds)}
                    </span>
                  </>
                ) : isEditing ? (
                  approveOnUpdate ? (
                    '✅ Update & Approve Story'
                  ) : (
                    '✏️ Update Story'
                  )
                ) : (
                  '➕ Add Story'
                )}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const StoryLoaderSpinner = () => (
  <span
    style={{
      width: '18px',
      height: '18px',
      borderRadius: '50%',
      border: '3px solid rgba(255,255,255,0.35)',
      borderTopColor: '#fff',
      display: 'inline-block',
      animation: 'storySpin 0.7s linear infinite',
      flexShrink: 0,
    }}
  />
);

const formatLoaderTime = (secs) => {
  const m = Math.floor(secs / 60);
  const s = secs % 60;
  return m > 0 ? `${m}:${String(s).padStart(2, '0')}` : `${s}s`;
};

const Label = ({ children, style }) => (
  <div
    style={{
      fontSize: '11px',
      fontWeight: '700',
      color: '#A3AED0',
      marginBottom: '5px',
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      ...style,
    }}
  >
    {children}
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    approved: { bg: '#01B57422', color: '#01B574', label: 'APPROVED' },
    pending: { bg: '#FFB54722', color: '#FFB547', label: 'PENDING' },
    rejected: { bg: '#EE5D5022', color: '#EE5D50', label: 'REJECTED' },
  };
  const s = map[status] || map.pending;
  return (
    <span
      style={{
        padding: '4px 10px',
        borderRadius: '6px',
        fontSize: '11px',
        background: s.bg,
        color: s.color,
        fontWeight: '700',
      }}
    >
      {s.label}
    </span>
  );
};

export default StoryManagementAdmin;

